/* ────────────────────────────────────────────────────────────────────────────
   Pure helpers for the "Template inputs" form on the campaign Settings step.

   Two operations:
     1. collectTemplateTokens — union the merge_fields of every template the
        campaign's sequence steps reference, returning a deduped list.
     2. categorizeTokens — group the remaining-to-fill tokens by their
        category (newsletter, story, feature, item, card, cta, job, event,
        deal, contact, generic) and decide whether each one should render
        as a textarea or a single-line input.

   Both pure — easy to unit-test, easy to reuse from the form, the preview
   surface, and any future automation that wants to know what a template
   asks for.
   ──────────────────────────────────────────────────────────────────────── */

/** Mirror of `AUTO_RESOLVED_TOKENS` in `apps/api/src/modules/email-send/
 *  merge-data.ts`. The drift guard test on the api side catches the case
 *  where the dict learns a key but this list doesn't, AND the api unit
 *  test catches the reverse — together they keep the two in lockstep. */
export const AUTO_RESOLVED_TOKENS: ReadonlySet<string> = new Set([
  // Recipient
  'first_name',
  'last_name',
  'full_name',
  'company',
  'company_name',
  'title',
  'role_name',
  'email',
  'recipient.email',
  // Recruiter
  'recruiter.full_name',
  'recruiter.first_name',
  'recruiter.last_name',
  'recruiter.email',
  'recruiter.title',
  'recruiter.phone',
  // Company
  'company.name',
  'company.address',
  // Calendar
  'calendar_link',
  'calendar_url',
  // Channel
  'unsubscribe_url',
  // Title / preheader passthrough — recruiter sets these directly on the
  // template/campaign, not in the inputs form.
  'preheader',
  'email_subject',
]);

export type TokenCategory =
  | 'newsletter'
  | 'story'
  | 'feature'
  | 'item'
  | 'card'
  | 'job'
  | 'event'
  | 'deal'
  | 'cta'
  | 'contact'
  | 'generic';

/** Display order — the form renders groups in this order. */
export const CATEGORY_ORDER: TokenCategory[] = [
  'newsletter',
  'feature',
  'story',
  'item',
  'card',
  'cta',
  'event',
  'deal',
  'job',
  'contact',
  'generic',
];

export const CATEGORY_LABELS: Record<TokenCategory, string> = {
  newsletter: 'Newsletter',
  feature: 'Featured story',
  story: 'Stories',
  item: 'Two-up items',
  card: 'Cards',
  cta: 'Call to action',
  event: 'Event',
  deal: 'Deal',
  job: 'Job',
  contact: 'Contact',
  generic: 'Other',
};

/** Map a token (e.g. `story_1.title`) to its category. */
export function categoryFor(token: string): TokenCategory {
  if (token.startsWith('newsletter.')) return 'newsletter';
  if (/^story_\d+\./.test(token) || token === 'story') return 'story';
  if (token.startsWith('feature.')) return 'feature';
  if (/^item_\d+\./.test(token)) return 'item';
  if (/^card_\d+\./.test(token)) return 'card';
  if (/^job_\d+\./.test(token) || token.startsWith('job.')) return 'job';
  if (token.startsWith('event.')) return 'event';
  if (token.startsWith('deal.')) return 'deal';
  if (token.startsWith('cta.')) return 'cta';
  if (token.startsWith('contact.')) return 'contact';
  return 'generic';
}

/** Long-form fields (body, intro, description, summary, tagline) render
 *  as textareas; everything else as single-line inputs. Heuristic on the
 *  trailing segment of the token name. */
export function isLongFormToken(token: string): boolean {
  const tail = token.split('.').pop() ?? token;
  return /^(body|intro|description|summary|tagline)$/i.test(tail);
}

export interface TemplateLike {
  /** What we need from the api `Template`. */
  id: string;
  name: string;
  merge_fields: string[];
}

export interface StepLike {
  /** Sequence step from the campaign. Steps without a template_id (free-
   *  text composed bodies) contribute nothing to the union. */
  template_id?: string | null;
}

/** Union of merge fields across every template referenced by the campaign's
 *  sequence steps. Deduplicates and preserves first-occurrence order. */
export function collectTemplateTokens(
  steps: StepLike[],
  templatesById: Map<string, TemplateLike>,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const step of steps) {
    if (!step.template_id) continue;
    const tmpl = templatesById.get(step.template_id);
    if (!tmpl) continue;
    for (const token of tmpl.merge_fields) {
      if (!seen.has(token)) {
        seen.add(token);
        out.push(token);
      }
    }
  }
  return out;
}

export interface CategorizedGroup {
  category: TokenCategory;
  label: string;
  tokens: string[];
}

/** Bindings the campaign has set up that auto-resolve a whole token family.
 *  Sprint 16c starts with `bound_job_id` for `{{job.*}}`; future sprints
 *  may add `bound_event_id`, `bound_deal_id`, multi-job bindings, etc. */
export interface CampaignBindings {
  /** When set, the `job.*` tokens (NOT `job_N.*`) resolve from JobCache
   *  at send time and the manual fields are hidden from the form. */
  bound_job_id?: string | null;
}

/** Tokens auto-resolved by a `bound_job_id`. Mirror of `JOB_BOUND_TOKENS`
 *  in `apps/api/src/modules/email-send/job-token-resolver.ts`. */
const JOB_BOUND_TOKENS: ReadonlySet<string> = new Set([
  'job.id',
  'job.title',
  'job.location',
  'job.status',
  'job.url',
  'job.summary',
  'job.salary',
  'job.type',
  'job.hero_image',
]);

/** Drop tokens the system already resolves, then group what's left by
 *  category in CATEGORY_ORDER. Empty groups are omitted from the result.
 *
 *  When `bindings.bound_job_id` is set, tokens in `JOB_BOUND_TOKENS` are
 *  also treated as resolved and hidden from the form. The picker UI
 *  collects the binding separately; the form only asks for what's left.
 *
 *  Note: `job_N.*` tokens (from multi-up newsletter layouts) are NOT
 *  affected by `bound_job_id` — they remain manual until a future
 *  multi-bind feature lands. */
export function categorizeTokens(
  tokens: string[],
  bindings: CampaignBindings = {},
): CategorizedGroup[] {
  const filtered = tokens.filter((t) => {
    if (AUTO_RESOLVED_TOKENS.has(t)) return false;
    if (bindings.bound_job_id && JOB_BOUND_TOKENS.has(t)) return false;
    return true;
  });
  const byCat = new Map<TokenCategory, string[]>();
  for (const token of filtered) {
    const cat = categoryFor(token);
    const list = byCat.get(cat) ?? [];
    list.push(token);
    byCat.set(cat, list);
  }
  return CATEGORY_ORDER
    .filter((cat) => (byCat.get(cat)?.length ?? 0) > 0)
    .map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      tokens: byCat.get(cat) ?? [],
    }));
}

/** Whether any of the unresolved tokens look like a `{{job.*}}` token
 *  (the single-job family, not job_N). Used by the campaign Settings
 *  page to decide whether to surface the JobPicker. */
export function templateUsesJobBinding(tokens: string[]): boolean {
  return tokens.some((t) => JOB_BOUND_TOKENS.has(t));
}

/** Tokens that need filling, given the campaign's current merge_context.
 *  A token "needs filling" when it's in the unresolved set AND the
 *  current value is empty (or undefined). Used to compute the "X fields
 *  remaining" badge on the Settings step. */
export function tokensStillMissing(
  unresolvedTokens: string[],
  mergeContext: Record<string, string>,
): string[] {
  return unresolvedTokens.filter((t) => !mergeContext[t]?.trim());
}
